import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import CreateOrderForm from './CreateOrder';
import { SortableTh } from '../components/SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import { fetchOrders, orderAssigneeNames, type OrderRow } from '../api/orderApi';
import {
  fetchInstallationAppointments,
  type InstallationAppointmentRow,
} from '../api/installationAppointments';
import './Page.css';
import './Orders.css';

function formatApptDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function InstallationAppointmentsTable({
  list,
  onRowClick,
}: {
  list: InstallationAppointmentRow[];
  onRowClick: (appt: InstallationAppointmentRow) => void;
}) {
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(list, sortKey, sortDir, (row, key) => {
        switch (key) {
          case 'business':
            return row.business_name ?? '';
          case 'order':
            return row.order_number ?? '';
          case 'installation':
            return row.installation_at ?? '';
          default:
            return '';
        }
      }),
    [list, sortKey, sortDir]
  );
  return (
    <div className="orders-table-wrapper">
      <table className="orders-table">
        <thead>
          <tr>
            <SortableTh label="Business" columnKey="business" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Order" columnKey="order" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Installation" columnKey="installation" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <th scope="col">Installers</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={4} className="orders-table-empty" data-label="">
                No scheduled installation appointments.
              </td>
            </tr>
          ) : (
            sorted.map((appt) => (
              <tr key={appt.id} onClick={() => onRowClick(appt)} style={{ cursor: 'pointer' }}>
                <td data-label="Business">{appt.business_name}</td>
                <td data-label="Order">{appt.order_number || '—'}</td>
                <td data-label="Installation">{formatApptDate(appt.installation_at)}</td>
                <td data-label="Installers">
                  {appt.assignees.length ? appt.assignees.map((a) => a.username).join(', ') : '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Order statuses in workflow order, Contract stage through Completed. */
const STATUS_ORDER = ['Pending', 'In Progress', 'Approved', 'Pilot', 'Awaiting Invoice', 'Invoice Ready', 'Scheduled', 'Completed'];

function compareStatuses(a: string, b: string): number {
  const ia = STATUS_ORDER.indexOf(a);
  const ib = STATUS_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
}

function statusBadgeClass(status: string): string {
  return `status-badge status-${String(status).toLowerCase().replace(/\s+/g, '-')}`;
}

const STATUS_MENU_WIDTH = 240;

/**
 * Status header that opens a menu with how many orders have each status, and
 * checkboxes that narrow the table to the checked statuses.
 */
function StatusFilterTh({
  options,
  selected,
  onChange,
  total,
  shown,
  sortKey,
  sortDir,
  onSort,
}: {
  options: { status: string; count: number }[];
  selected: string[];
  onChange: (next: string[]) => void;
  total: number;
  shown: number;
  sortKey: string | null;
  sortDir: 'asc' | 'desc';
  onSort: (key: string, dir: 'asc' | 'desc') => void;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const open = menuPos !== null;
  const sorted = sortKey === 'status';
  const filtered = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    // The menu sits at the end of the page, so keyboard users are moved into it.
    const menu = menuRef.current;
    const first = menu?.querySelector<HTMLElement>('input[type="checkbox"]') ?? menu?.querySelector<HTMLElement>('button');
    first?.focus({ preventScroll: true });

    const close = () => setMenuPos(null);
    const isInside = (target: EventTarget | null) =>
      target instanceof Node && (!!btnRef.current?.contains(target) || !!menuRef.current?.contains(target));
    const onPointerOrFocus = (e: Event) => {
      if (!isInside(e.target)) close();
    };
    // The menu is pinned where the header was when it opened.
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      btnRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointerOrFocus);
    document.addEventListener('focusin', onPointerOrFocus);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointerOrFocus);
      document.removeEventListener('focusin', onPointerOrFocus);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggleMenu = () => {
    if (open || !btnRef.current) {
      setMenuPos(null);
      return;
    }
    const rect = btnRef.current.getBoundingClientRect();
    const top = rect.bottom + 6;
    setMenuPos({
      top,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - STATUS_MENU_WIDTH - 8)),
      maxHeight: Math.max(180, window.innerHeight - top - 12),
    });
  };

  const toggleStatus = (status: string) =>
    onChange(selected.includes(status) ? selected.filter((s) => s !== status) : [...selected, status]);

  const sortButton = (dir: 'asc' | 'desc', label: string) => (
    <button
      type="button"
      className={sorted && sortDir === dir ? 'is-active' : undefined}
      onClick={() => {
        onSort('status', dir);
        setMenuPos(null);
        btnRef.current?.focus();
      }}
    >
      {label}
    </button>
  );

  return (
    <th scope="col" className="sortable-th" aria-sort={sorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        ref={btnRef}
        type="button"
        className={`sortable-th-btn orders-status-th-btn${filtered ? ' orders-status-th-btn--filtered' : ''}`}
        onClick={toggleMenu}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={filtered ? `Status, ${selected.length} selected` : undefined}
        title="Filter by status"
      >
        <span className="sortable-th-label">Status</span>
        {filtered && (
          <span className="orders-status-th-count" aria-hidden>
            {selected.length}
          </span>
        )}
        {sorted && (
          <span className="sortable-th-icon" aria-hidden>
            {sortDir === 'asc' ? '↑' : '↓'}
          </span>
        )}
        <span className="orders-status-th-caret" aria-hidden>
          ▾
        </span>
      </button>
      {menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="orders-status-menu"
            role="dialog"
            aria-label="Filter by status"
            style={{ top: menuPos.top, left: menuPos.left, width: STATUS_MENU_WIDTH, maxHeight: menuPos.maxHeight }}
          >
            <div className="orders-status-menu-sort">
              {sortButton('asc', 'Sort A to Z')}
              {sortButton('desc', 'Sort Z to A')}
            </div>
            {options.length === 0 ? (
              <p className="orders-status-menu-empty">No orders in this table.</p>
            ) : (
              options.map(({ status, count }) => (
                <label key={status} className="orders-status-menu-option">
                  <input type="checkbox" checked={selected.includes(status)} onChange={() => toggleStatus(status)} />
                  <span className={statusBadgeClass(status)}>{status || 'No status'}</span>
                  <span className="orders-status-menu-count">{count}</span>
                </label>
              ))
            )}
            <div className="orders-status-menu-total">
              <span>{filtered ? 'Showing' : 'Total'}</span>
              <span>{filtered ? `${shown} of ${total}` : total}</span>
            </div>
            {filtered && (
              <button type="button" className="orders-status-menu-clear" onClick={() => onChange([])}>
                Clear filter
              </button>
            )}
          </div>,
          document.body
        )}
    </th>
  );
}

function OrdersSectionTable({
  list,
  emptyMessage,
  onOrderClick,
  statusFilter,
  onStatusFilterChange,
}: {
  list: OrderRow[];
  emptyMessage: string;
  onOrderClick: (orderNumber: string) => void;
  statusFilter: string[];
  onStatusFilterChange: (next: string[]) => void;
}) {
  const { sortKey, sortDir, toggleSort, setSort } = useTableSortState();
  const statusOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const order of list) counts.set(order.status ?? '', (counts.get(order.status ?? '') ?? 0) + 1);
    // A checked status the search has emptied stays listed so it can still be unchecked.
    for (const status of statusFilter) if (!counts.has(status)) counts.set(status, 0);
    return Array.from(counts, ([status, count]) => ({ status, count })).sort((a, b) =>
      compareStatuses(a.status, b.status)
    );
  }, [list, statusFilter]);
  const visible = useMemo(
    () => (statusFilter.length === 0 ? list : list.filter((order) => statusFilter.includes(order.status ?? ''))),
    [list, statusFilter]
  );
  const sorted = useMemo(
    () =>
      sortRows(visible, sortKey, sortDir, (row, key) => {
        switch (key) {
          case 'order_number':
            return row.order_number ?? '';
          case 'company_name':
            return row.company_name ?? '';
          case 'status':
            return row.status ?? '';
          case 'employee_name':
            return orderAssigneeNames(row).join(', ');
          default:
            return '';
        }
      }),
    [visible, sortKey, sortDir]
  );

  return (
    <div className="orders-table-wrapper">
      <table className="orders-table">
        <thead>
          <tr>
            <SortableTh label="Order Number" columnKey="order_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <SortableTh label="Company Name" columnKey="company_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            <StatusFilterTh
              options={statusOptions}
              selected={statusFilter}
              onChange={onStatusFilterChange}
              total={list.length}
              shown={visible.length}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={setSort}
            />
            <SortableTh label="Employee" columnKey="employee_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={4} className="orders-table-empty" data-label="">
                {list.length > 0 ? 'No orders match the selected statuses.' : emptyMessage}
              </td>
            </tr>
          ) : (
            sorted.map((order) => {
              return (
                <tr
                  key={order.id}
                  onClick={() => onOrderClick(order.order_number)}
                  style={{ cursor: 'pointer' }}
                >
                  <td data-label="Order Number">{order.order_number}</td>
                  <td data-label="Company">{order.company_name}</td>
                  <td data-label="Status">
                    <span className={statusBadgeClass(order.status)}>{order.status}</span>
                  </td>
                  <td data-label="Employee">{orderAssigneeNames(order).join(', ') || 'unassigned'}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

type StatusFilterSection = 'contracts' | 'invoice' | 'completed' | 'voided';

function matchesOrderSearch(order: OrderRow, search: string): boolean {
  if (!search.trim()) return true;
  const term = search.trim().toLowerCase();
  const numberMatch = (order.order_number ?? '').toLowerCase().includes(term);
  const companyMatch = (order.company_name ?? '').toLowerCase().includes(term);
  return numberMatch || companyMatch;
}

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [installationAppts, setInstallationAppts] = useState<InstallationAppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderSearch, setOrderSearch] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    contracts: false,
    invoice: false,
    installation: false,
    completed: false,
    voided: false,
  });

  // Held here so collapsing a section keeps its status filter.
  const [statusFilters, setStatusFilters] = useState<Record<StatusFilterSection, string[]>>({
    contracts: [],
    invoice: [],
    completed: [],
    voided: [],
  });
  const statusFilterProps = (section: StatusFilterSection) => ({
    statusFilter: statusFilters[section],
    onStatusFilterChange: (next: string[]) => setStatusFilters((prev) => ({ ...prev, [section]: next })),
  });

  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [list, appts] = await Promise.all([
          fetchOrders(),
          fetchInstallationAppointments().catch(() => ({ scheduled: [], completed: [], void: [] })),
        ]);
        if (!cancelled) {
          setOrders(list);
          setInstallationAppts(appts.scheduled);
        }
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Voided orders live in their own table; keep them out of the stage tables.
  const activeOrders = orders.filter((o) => !o.is_voided);
  const voidedOrders = orders.filter((o) => o.is_voided);

  const contractsOrders = activeOrders.filter((o) => o.category === 'Contract');
  const inventoryOrders = activeOrders.filter((o) => o.category === 'Inventory');
  const completedOrders = activeOrders.filter((o) => o.category === 'Completed');

  // The Installation section lists scheduled appointments created from the "Set Up
  // Installation Appointment" button (i.e. those linked to an order).
  const filteredInstallationAppts = useMemo(() => {
    const term = orderSearch.trim().toLowerCase();
    return installationAppts
      .filter((a) => !!a.order_number)
      .filter(
        (a) =>
          !term ||
          (a.business_name ?? '').toLowerCase().includes(term) ||
          (a.order_number ?? '').toLowerCase().includes(term)
      );
  }, [installationAppts, orderSearch]);

  const filteredContracts = contractsOrders.filter((o) => matchesOrderSearch(o, orderSearch));
  const filteredInventory = inventoryOrders.filter((o) => matchesOrderSearch(o, orderSearch));
  const filteredCompleted = completedOrders.filter((o) => matchesOrderSearch(o, orderSearch));
  const filteredVoided = voidedOrders.filter((o) => matchesOrderSearch(o, orderSearch));

  const handleOrderClick = (orderNumber: string) => {
    navigate(`/orders/${orderNumber}`);
  };

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">

          <div>
            <PageHeader title="Orders" subtitle="Manage and track all orders" />
          </div>

          <div className="page-content">

            <div className="page-toolbar">
              <input
                type="text"
                className="page-toolbar-search"
                placeholder="Search by order number or company name..."
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                aria-label="Search orders"
              />
              <button type="button" className="page-toolbar-btn" onClick={() => setCreateModalOpen(true)}>
                + Add Order
              </button>
            </div>

            {loading ? (
              <p className="page-subtitle">Loading orders...</p>
            ) : (
              <>
                <div className="orders-tables-container">
                  <div className="orders-table-section">
                    <div className="orders-collapsible-header" onClick={() => toggleSection('contracts')}>
                      <h3 className="orders-table-title">Contracts</h3>
                      <span className={`orders-collapse-arrow${!collapsedSections.contracts ? ' orders-collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    {!collapsedSections.contracts && (
                      <OrdersSectionTable
                        list={filteredContracts}
                        emptyMessage="No contract orders."
                        onOrderClick={handleOrderClick}
                        {...statusFilterProps('contracts')}
                      />
                    )}
                  </div>

                  <div className="orders-table-section">
                    <div className="orders-collapsible-header" onClick={() => toggleSection('invoice')}>
                      <h3 className="orders-table-title">Invoice</h3>
                      <span className={`orders-collapse-arrow${!collapsedSections.invoice ? ' orders-collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    {!collapsedSections.invoice && (
                      <OrdersSectionTable
                        list={filteredInventory}
                        emptyMessage="No invoice orders."
                        onOrderClick={handleOrderClick}
                        {...statusFilterProps('invoice')}
                      />
                    )}
                  </div>

                  <div className="orders-table-section">
                    <div className="orders-collapsible-header" onClick={() => toggleSection('installation')}>
                      <h3 className="orders-table-title">Installation</h3>
                      <span className={`orders-collapse-arrow${!collapsedSections.installation ? ' orders-collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    {!collapsedSections.installation && (
                      <InstallationAppointmentsTable
                        list={filteredInstallationAppts}
                        onRowClick={(appt) =>
                          appt.order_number
                            ? navigate(`/orders/${appt.order_number}`)
                            : navigate(`/installation/${appt.id}`)
                        }
                      />
                    )}
                  </div>

                  <div className="orders-table-section">
                    <div className="orders-collapsible-header" onClick={() => toggleSection('completed')}>
                      <h3 className="orders-table-title">Completed Orders</h3>
                      <span className={`orders-collapse-arrow${!collapsedSections.completed ? ' orders-collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    {!collapsedSections.completed && (
                      <OrdersSectionTable
                        list={filteredCompleted}
                        emptyMessage="No completed orders."
                        onOrderClick={handleOrderClick}
                        {...statusFilterProps('completed')}
                      />
                    )}
                  </div>

                  <div className="orders-table-section">
                    <div className="orders-collapsible-header" onClick={() => toggleSection('voided')}>
                      <h3 className="orders-table-title">Voided Orders</h3>
                      <span className={`orders-collapse-arrow${!collapsedSections.voided ? ' orders-collapse-arrow--open' : ''}`}>▶</span>
                    </div>
                    {!collapsedSections.voided && (
                      <OrdersSectionTable
                        list={filteredVoided}
                        emptyMessage="No voided orders."
                        onOrderClick={handleOrderClick}
                        {...statusFilterProps('voided')}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create New Order"
        wide
      >
        <CreateOrderForm onClose={() => setCreateModalOpen(false)} />
      </Modal>
    </div>
  );
};

export default Orders;

